import React, { useEffect, useState } from 'react';
import CustomTh from '../../../../../components/table/CustomTh';
import CustomTd from '../../../../../components/table/CustomTd';
import TableFunction from '../../../../../components/table/TableFunction';
import { useTranslation } from 'react-i18next';
import {
    GetHidRecordDetails
} from '../../../../../reduxToolkit/HID/HidApi';
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { TABLES } from "../../../../../Apis/Tables";
import {EMPTY_VALUES, errorMsg} from "../../../../../Apis/functions";
import { toast } from "react-toastify";
import { ACR_MODEL } from "../../Acrs/Model/Models";
import {DeleteItemsApi} from "../../../../../reduxToolkit/Commons/CommonsApi";

/**
 * @author Karen Yunuen Pozos Arias
 * @position Full Stack Developer - IBL
 * @contact kpozos@ibl.mx
 *
 * @description
 * Displays and manages the Access Control Readers (ACRs) table within a specific controller.
 * It allows deletion of ACRs and automatically refreshes when the data source or configuration changes.
 *
 * @param {Array} defaultSioList - Default list of SIOs containing their associated ACRs.
 *
 * @returns {Object} - Returns table-related handlers, configuration, and state references.
 */
const AcrTableApp = ({ defaultSioList }) => {
    const { t } = useTranslation();
    const { id } = useParams();
    const dispatch = useDispatch();

    const [tableId, setTableId] = useState("tbl_ota_table");
    const [deleteShow, setDeleteShow] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [selectedList, setSelectedList] = useState([]);
    const [isAllChecked, setIsAllChecked] = useState(false);
    const [results, setResults] = useState(null);
    const [modified, setModified] = useState(false);

    /**
     * Initializes table data from the default SIO list.
     * Converts ACR objects into a table-friendly format using ACR_MODEL.
     */
    useEffect(() => {
        if (defaultSioList?.length > 0) {
            const aux = [];
            defaultSioList.forEach((row) => {
                row.acrs.forEach((e) => {
                    aux.push(ACR_MODEL.setToTable(e, row.name));
                });
            });
            setResults({ content: aux, totalElements: aux.length });
        }
    }, [defaultSioList]);

    /**
     * Builds the table header elements for ACR attributes.
     *
     * @returns {Array<JSX.Element>} Array of <CustomTh> components defining column headers.
     */
    const getThead = () => {
        return [
            <CustomTh tableId={tableId} className='first_head'>{t('zone')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('entry_reader')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('entry_output')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('door_input')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('exit_reader')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('exit_output')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('exit_input')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('sio')}</CustomTh>
        ];
    };

    /**
     * Builds the table body elements displaying ACR details.
     *
     * @returns {Object} Object containing the rendered JSX body and its corresponding raw data.
     */
    const getTbody = () => {
        if (!results || !results?.content || results?.content?.length === 0)
            return { body: [], data: [] };

        const body = results?.content?.map((item) => (
            <>
                <CustomTd tableId={tableId} classes="first-column">{item?.zoneName}</CustomTd>
                <CustomTd tableId={tableId}>{item?.entryReaderName}</CustomTd>
                <CustomTd tableId={tableId}>{item?.entryOutputName}</CustomTd>
                <CustomTd tableId={tableId}>{item?.doorInputName}</CustomTd>
                <CustomTd tableId={tableId}>{item?.exitReaderName || EMPTY_VALUES.DEFAULT}</CustomTd>
                <CustomTd tableId={tableId}>{item?.exitOutputName || EMPTY_VALUES.DEFAULT}</CustomTd>
                <CustomTd tableId={tableId}>{item?.exitInputName || EMPTY_VALUES.DEFAULT}</CustomTd>
                <CustomTd tableId={tableId}>{item?.hidSioName}</CustomTd>
            </>
        ));

        return {
            body,
            data: results?.content
        };
    };

    /**
     * Executes a paginated search request using basic criteria.
     *
     * Builds the request body with the current page, page size, selected model,
     * and any additional criteria passed as arguments.
     * Triggers the search action through Redux and updates the results on success.
     */
    const getData = () => {
        dispatch(GetHidRecordDetails({ id })).then(({ payload: { data: { data, success } } }) => {
            if (success) {
                const aux = [];
                data?.sios.forEach((row) => {
                    row.acrs.forEach((e) => {
                        aux.push(ACR_MODEL.setToTable(e, row.name));
                    });
                });
                setResults({ content: aux, totalElements: aux.length });
            }
        });
    }

    /**
     * Table utility hook to manage selection, deletion, and rendering logic.
     */
    const {
        thead,
        tbody,
        handleCheckboxChange,
        handleDeleteAll
    } = TableFunction({
        selectedList,
        setSelectedList,
        data: results,
        getTbody,
        getThead,
        setIsAllChecked,

        getData,//New
        modified,//New
        setModified,//New
    });

    /**
     * Deletes all currently selected ACRs from the database.
     * After successful deletion, resets selection and refreshes table data.
     */
    const deleteSelected = () => {
        const body = selectedList;
        if (body.length > 0) {
            dispatch(DeleteItemsApi({ tableName: TABLES.HID_ACR, body }))
                .then(({ payload: { data: { success, code } } }) => {
                    if (success === true) {
                        setSelectedList([]);
                        setIsAllChecked(false);
                        setModified(true);
                    }else errorMsg(code);
                });
        }
    };

    return {
        t,
        id,
        thead,
        tbody,
        tableId,
        selectedList,
        isAllChecked,
        deleteShow,
        setDeleteShow,
        showCreateModal,
        setShowCreateModal,
        setModified,
        handleCheckboxChange,
        handleDeleteAll,
        deleteSelected
    };
};

export default AcrTableApp;
